/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.2.3): util/Design-factory.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import { DefaultAllowlist, sanitizeHtml } from './sanitizer'
import { getElement, isElement } from '../util/index'
import SelectorEngine from '../dom/selector-engine'
import Config from './config'

/**
 * Constants
 */

const NAME = 'DesignFactory'

const Default = {
  allowList: DefaultAllowlist,
  content: {}, // { selector : text ,  selector2 : text2 , }
  extraClass: '',
  html: false,
  sanitize: true,
  sanitizeFn: null,
  Design: '<div></div>'
}

const DefaultType = {
  allowList: 'object',
  content: 'object',
  extraClass: '(string|function)',
  html: 'boolean',
  sanitize: 'boolean',
  sanitizeFn: '(null|function)',
  Design: 'string'
}

const DefaultContentType = {
  entry: '(string|element|function|null)',
  selector: '(string|element)'
}

/**
 * Class definition
 */

class DesignFactory extends Config {
  constructor(config) {
    super()
    this._config = this._getConfig(config)
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public
  getContent() {
    return Object.values(this._config.content)
      .map(config => this._resolvePossibleFunction(config))
      .filter(Boolean)
  }

  hasContent() {
    return this.getContent().length > 0
  }

  changeContent(content) {
    this._checkContent(content)
    this._config.content = { ...this._config.content, ...content }
    return this
  }

  toHtml() {
    const DesignWrapper = document.createElement('div')
    DesignWrapper.innerHTML = this._maybeSanitize(this._config.Design)

    for (const [selector, text] of Object.entries(this._config.content)) {
      this._setContent(DesignWrapper, text, selector)
    }

    const Design = DesignWrapper.children[0]
    const extraClass = this._resolvePossibleFunction(this._config.extraClass)

    if (extraClass) {
      Design.classList.add(...extraClass.split(' '))
    }

    return Design
  }

  // Private
  _typeCheckConfig(config) {
    super._typeCheckConfig(config)
    this._checkContent(config.content)
  }

  _checkContent(arg) {
    for (const [selector, content] of Object.entries(arg)) {
      super._typeCheckConfig({ selector, entry: content }, DefaultContentType)
    }
  }

  _setContent(Design, content, selector) {
    const DesignElement = SelectorEngine.findOne(selector, Design)

    if (!DesignElement) {
      return
    }

    content = this._resolvePossibleFunction(content)

    if (!content) {
      DesignElement.remove()
      return
    }

    if (isElement(content)) {
      this._putElementInDesign(getElement(content), DesignElement)
      return
    }

    if (this._config.html) {
      DesignElement.innerHTML = this._maybeSanitize(content)
      return
    }

    DesignElement.textContent = content
  }

  _maybeSanitize(arg) {
    return this._config.sanitize ? sanitizeHtml(arg, this._config.allowList, this._config.sanitizeFn) : arg
  }

  _resolvePossibleFunction(arg) {
    return typeof arg === 'function' ? arg(this) : arg
  }

  _putElementInDesign(element, DesignElement) {
    if (this._config.html) {
      DesignElement.innerHTML = ''
      DesignElement.append(element)
      return
    }

    DesignElement.textContent = element.textContent
  }
}

export default DesignFactory
